import { getApiBaseUrl } from "../apiClient.js";

// The commit this web build was built from — set at build time (Render provides
// the SHA). Stamped on every report so a fix targets the exact code that ran.
const RELEASE_SHA = (import.meta.env.VITE_RELEASE_SHA as string | undefined) ?? undefined;

// A pathological render loop could call this thousands of times; cap per page
// load so a runaway never hammers the network. The server dedupes identical
// crashes and rate-limits by IP, so the client needs no smarter throttle.
const MAX_REPORTS_PER_SESSION = 50;
let sent = 0;

// Under the `vite` dev server (the only place with HMR) a "crash" is almost
// always a mid-edit state — a half-renamed variable, a hook added while the page
// is live. A local build is often pointed at the production API, whose deployed
// capture would persist that noise into the owner's triage queue (it did: every
// web error in the queue came from localhost:5173 with HMR `?t=` stamps). So the
// dev server (mode "development") never reports, and neither does any build
// served from this machine (a `vite preview` of a production build).
const RUNNING_ON_DEV_SERVER = import.meta.env.MODE === "development";

function servedLocally(): boolean {
  if (typeof location === "undefined") return false;
  const host = location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]" || host.endsWith(".localhost");
}

/**
 * Best-effort crash reporter → POST /api/errors. Never throws and never blocks
 * (fire-and-forget). Attaches the build's commit SHA and the current URL. The
 * report is deduped and rate-limited server-side.
 */
export function reportError(
  error: unknown,
  context?: { route?: string; extra?: Record<string, unknown> }
): void {
  try {
    if (RUNNING_ON_DEV_SERVER || servedLocally() || sent >= MAX_REPORTS_PER_SESSION) return;
    sent += 1;
    const err = error instanceof Error ? error : new Error(String(error));
    const body = JSON.stringify({
      kind: err.name,
      message: err.message,
      stack: err.stack,
      route: context?.route,
      url: typeof location !== "undefined" ? location.href : undefined,
      releaseSha: RELEASE_SHA,
      context: context?.extra
    });
    // keepalive lets the report survive a navigation or tab close.
    void fetch(`${getApiBaseUrl()}/api/errors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true
    }).catch(() => {
      // Reporting a crash must never surface its own failure.
    });
  } catch {
    // Building/serializing the report failed — swallow; a report is best-effort.
  }
}

let installed = false;

/**
 * Installs window-level handlers so uncaught errors and unhandled promise
 * rejections (which a React error boundary can't catch) also report. Idempotent.
 */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (event) => {
    reportError(event.error ?? new Error(event.message), { route: "window.onerror" });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    reportError(reason instanceof Error ? reason : new Error(String(reason)), {
      route: "unhandledrejection"
    });
  });
}
